import { useEffect, useRef } from 'react';
import { MeetingStatus } from 'amazon-chime-sdk-component-library-react';
import { toast } from 'react-toastify';
import i18n from '../../../i18n/i18n';

const useAudioOutputRecovery = ({
  meetingStatus,
  meetingManager,
  initialAudioOutputDeviceId,
  onAudioOutputSelected,
}) => {
  const knownOutputIdsRef = useRef(new Set());
  const currentOutputDeviceIdRef = useRef(null);
  const lastToastAtRef = useRef(0);
  const initialSyncDoneRef = useRef(false);
  const didPrimeKnownIdsRef = useRef(false);

  useEffect(() => {
    if (meetingStatus !== MeetingStatus.Succeeded) {
      return () => {};
    }

    let cancelled = false;
    let applying = false;
    let pending = false;
    let deviceChangeTimer = null;
    let suppressNotFoundErrorUntil = 0;

    const applyBestAudioOutput = async () => {
      if (cancelled || applying) {
        pending = true;
        return;
      }

      applying = true;
      try {
        const av = meetingManager.meetingSession?.audioVideo;
        if (!av) {
          return;
        }

        const outputs = await av.listAudioOutputDevices();
        if (!outputs?.length) {
          return;
        }

        const currentOutputDeviceId = currentOutputDeviceIdRef.current;
        const previousIds = knownOutputIdsRef.current;
        const currentIds = new Set(outputs.map((device) => device.deviceId));
        const addedDevices = didPrimeKnownIdsRef.current
          ? outputs.filter((device) => !previousIds.has(device.deviceId))
          : [];

        const exists = (deviceId) => currentIds.has(deviceId);
        const preferredId = addedDevices.length
          ? addedDevices[addedDevices.length - 1].deviceId
          : (exists(currentOutputDeviceId)
            ? currentOutputDeviceId
            : (initialAudioOutputDeviceId && exists(initialAudioOutputDeviceId)
              ? initialAudioOutputDeviceId
              : outputs[0].deviceId));

        const preferredDevice = outputs.find((device) => device.deviceId === preferredId);

        knownOutputIdsRef.current = currentIds;
        didPrimeKnownIdsRef.current = true;

        if (!preferredId || preferredId === currentOutputDeviceId) {
          return;
        }

        if (typeof meetingManager.startAudioOutputDevice === 'function') {
          await meetingManager.startAudioOutputDevice(preferredId);
        } else if (typeof av.chooseAudioOutputDevice === 'function') {
          await av.chooseAudioOutputDevice(preferredId);
        }

        currentOutputDeviceIdRef.current = preferredId;
        if (typeof onAudioOutputSelected === 'function') {
          onAudioOutputSelected(preferredId);
        }

        if (addedDevices.length > 0 && initialSyncDoneRef.current) {
          const nowMs = Date.now();
          if (nowMs - lastToastAtRef.current > 2500) {
            lastToastAtRef.current = nowMs;
            const label = preferredDevice?.label || i18n.t('VideoChatV2.audio.newAudioOutput');
            toast.info(i18n.t('VideoChatV2.audio.audioOutputSwitchedTo', { label }), {
              autoClose: 2500,
              position: 'bottom-right',
            });
          }
        }
      } catch (error) {
        const message = `${error?.message || ''}`.toLowerCase();
        const isNotFound = message.includes('requested device not found') || message.includes('not found');
        const now = Date.now();

        if (isNotFound) {
          // Device-change events can arrive before the new sink is fully
          // available to the browser. Retry on the next queued cycle.
          suppressNotFoundErrorUntil = now + 2000;
          pending = true;
        } else if (now > suppressNotFoundErrorUntil) {
          // Non-fatal.
          // eslint-disable-next-line no-console
          console.warn('[AudioOutputRecovery] Failed to switch output device.', error);
        }
      } finally {
        applying = false;
        if (pending && !cancelled) {
          pending = false;
          applyBestAudioOutput();
        }
        initialSyncDoneRef.current = true;
      }
    };

    applyBestAudioOutput();

    const handleDeviceChange = () => {
      if (deviceChangeTimer) {
        clearTimeout(deviceChangeTimer);
      }
      deviceChangeTimer = setTimeout(() => {
        deviceChangeTimer = null;
        applyBestAudioOutput();
      }, 180);
    };

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    return () => {
      cancelled = true;
      if (deviceChangeTimer) {
        clearTimeout(deviceChangeTimer);
        deviceChangeTimer = null;
      }
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [meetingStatus, meetingManager, initialAudioOutputDeviceId, onAudioOutputSelected]);
};

export default useAudioOutputRecovery;
